import { Component, inject, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { CentrexUsersService } from 'src/app/services/centrex-users-service';
import { CentrexUserModel } from 'src/app/models/centrex-user.model';
import { CentrexUsersFormComponent } from './centrex-users-form/centrex-users-form.component';
import { UsersService } from 'src/app/services/users.service';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { CentrexExtensionsService } from 'src/app/services/centrex-extensions.service';
import { CentrexExtensionModel } from 'src/app/models/centrex-extension.model';
import { catchError, defer, forkJoin, of } from 'rxjs';
import { AccessRightsService } from 'src/app/services/access-rights.service';
import { OrganizationsService } from 'src/app/services/organization.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { CentrexSitesService } from 'src/app/services/centrex-sites.service';
import { PlatformService } from 'src/app/services/platform.service';

interface ExtensionWithUserInfo extends CentrexExtensionModel {
  firstName?: string;
  lastName?: string;
  email?: string;
  siteName?: string;
}

@Component({
  selector: 'app-centrex-users',
  templateUrl: './centrex-users.component.html',
  styleUrl: './centrex-users.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatPaginatorModule,
    MatTableModule,
    NgClass,
    MatSortModule,
    NgxSpinnerModule,
  ],
})
export class CentrexUsersComponent implements OnInit {
  private spinner = inject(NgxSpinnerService);

  displayedColumns: string[] = [
    'name',
    'prenom',
    'number',
    'siteId',
    'account',
    'email',
    'blocked',
    'actions',
  ];

  dataSource = new MatTableDataSource<CentrexUserModel>();
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  userInfosMap: Map<string, ExtensionWithUserInfo> = new Map();

  filterValues: any = {
    name: '',
    prenom: '',
    account: '',
    email: '',
    number: '',
    siteId: '',
  };

  platform: string = this.platformService.getCurrentPlatform();
  isFullAccess = false;

  constructor(
    private centrexUsersService: CentrexUsersService,
    private dialog: MatDialog,
    private usersService: UsersService,
    private centrexExtensionsService: CentrexExtensionsService,
    private accessRightsService: AccessRightsService,
    private organizationsService: OrganizationsService,
    private toastr: ToastrService,
    private centrexSitesService: CentrexSitesService,
    private platformService: PlatformService,
  ) {}

  ngOnInit(): void {
    this.accessRightsService
      .hasNexusCentrexFullAccess()
      .subscribe((isAllowed) => (this.isFullAccess = isAllowed));

    this.organizationsService.currentOrganizationChange$.subscribe((org) => {
      if (org) {
        this.loadUsers(org.id);
      }
    });

    // const currentOrg = this.organizationsService.getCurrentOrganization();
    // if (currentOrg) {
    //   this.loadUsers(currentOrg.id);
    // }
  }

  initPlatform(): void {
    this.platform = this.platformService.getCurrentPlatform();
  }

  applyFilter(column: string, value: string): void {
    this.filterValues[column] = value.trim().toLowerCase();
    this.dataSource.filter = JSON.stringify(this.filterValues);
  }

  customFilter = (data: CentrexUserModel, filter: string): boolean => {
    const searchTerms = JSON.parse(filter);

    const nom = this.getNom(data).toLowerCase();
    const prenom = this.getPrenom(data).toLowerCase();
    const email = this.getEmail(data).toLowerCase();
    const siteId = this.getSiteName(data).toLowerCase();

    return (
      nom.includes(searchTerms.name) &&
      prenom.includes(searchTerms.prenom) &&
      email.includes(searchTerms.email) &&
      (data.account || '').toLowerCase().includes(searchTerms.account) &&
      (data.number || '').toLowerCase().includes(searchTerms.number) &&
      siteId.includes(searchTerms.siteId)
    );
  };

  onPlatformChange(): void {
    this.platformService.setPlatform(this.platform);
    const currentOrg = this.organizationsService.getCurrentOrganization();
    if (currentOrg) {
      this.loadUsers(currentOrg.id);
    }
  }

loadUsers(orgId: string): void {
  this.spinner.show('spinner');

  const platform = this.platformService.getCurrentPlatform();

  // =========================
  // ENREACH
  // =========================
  if (platform === 'Centrex Enreach') {

    forkJoin({
      users: this.centrexUsersService.getAllCentrexUsers(
        orgId,
        platform,
      ),

      sites: this.centrexSitesService.getSites(
        orgId,
        platform,
      ),
    }).subscribe({
      next: ({ users, sites }) => {

        const siteMap = new Map(
          sites.map((s: any) => [
            String(s.siteID || s.siteId),
            s.name,
          ]),
        );

        // enrich users directement
        const enrichedUsers = users.map((u: any) => ({
          ...u,
          siteName:
            siteMap.get(String(u.siteId)) || '—',
        }));

        this.dataSource =
          new MatTableDataSource(enrichedUsers);

        this.dataSource.paginator =
          this.paginator;

        this.dataSource.sort =
          this.sort;

        this.dataSource.filterPredicate =
          this.customFilter;

        this.spinner.hide('spinner');
      },

      error: (err) => {
        console.error(err);
        this.spinner.hide('spinner');
      },
    });

    return;
  }

  // =========================
  // NG
  // =========================
  forkJoin({

    extensions:
      this.centrexExtensionsService.getExtensions(
        orgId,
        platform,
      ),

    gumUsers:
      this.usersService
        .getUsers()
        .pipe(
          catchError(() => of([])),
        ),

    centrexUsers:
      this.centrexUsersService.getAllCentrexUsers(
        orgId,
        platform,
      ),

    sites:
      this.centrexSitesService.getSites(
        orgId,
        platform,
      ),

  }).subscribe({

    next: ({
      extensions,
      gumUsers,
      centrexUsers,
      sites,
    }) => {

      const userMap = new Map<string, any>();

      gumUsers.forEach((user) =>
        userMap.set(user.id, user),
      );

      const siteMap = new Map(
        sites.map((s: any) => [
          String(s.siteID || s.siteId),
          s.name,
        ]),
      );

      this.userInfosMap = new Map();

      extensions
        .filter(
          (e) => e.type === 'user' && e.userId,
        )
        .forEach((extension) => {

          const user =
            userMap.get(extension.userId!);

          const getProp = (key: string) =>
            user?.properties?.find(
              (p: any) => p.name === key,
            )?.value ?? '----';

          this.userInfosMap.set(extension.id, {
            ...extension,

            firstName:
              getProp('Firstname'),

            lastName:
              getProp('Lastname'),

            email:
              getProp('Email'),

            siteName:
              siteMap.get(
                String(extension.siteId),
              ) || '—',
          });
        });

      this.dataSource =
        new MatTableDataSource(
          centrexUsers,
        );

      this.dataSource.paginator =
        this.paginator;

      this.dataSource.sort =
        this.sort;

      this.dataSource.filterPredicate =
        this.customFilter;

      this.spinner.hide('spinner');
    },

    error: (err) => {
      console.error(err);
      this.spinner.hide('spinner');
    },
  });
}

  getSiteName(user: CentrexUserModel): string {
    if (this.platform === 'Centrex NG') {
      return (this.userInfosMap.get(user.id) as any)?.siteName || '—';
    } else {
      return (user as any).siteName || user.siteId || '—';
    }
  }

  onSort(event: { active: string; direction: string }): void {
    const { active, direction } = event;

    if (!direction) return;

    const sorted = [...this.dataSource.data].sort((a, b) => {
      let aValue: string | number = '';
      let bValue: string | number = '';

      switch (active) {
        case 'name':
          aValue = this.getNom(a).toLowerCase();
          bValue = this.getNom(b).toLowerCase();
          break;
        case 'prenom':
          aValue = this.getPrenom(a).toLowerCase();
          bValue = this.getPrenom(b).toLowerCase();
          break;
        case 'email':
          aValue = this.getEmail(a).toLowerCase();
          bValue = this.getEmail(b).toLowerCase();
          break;
        default:
          aValue = (a as any)[active] ?? '';
          bValue = (b as any)[active] ?? '';
          break;
      }

      return direction === 'asc'
        ? aValue < bValue
          ? -1
          : aValue > bValue
            ? 1
            : 0
        : aValue > bValue
          ? -1
          : aValue < bValue
            ? 1
            : 0;
    });

    this.dataSource.data = sorted;
  }

  openDialog(mode: 'view' | 'edit', user: CentrexUserModel): void {
    const enrichedUser = {
      ...user,
      siteName: this.getSiteName(user), // 🔥 ICI LE FIX
    };

    const enriched = this.userInfosMap.get(user.id);

    this.dialog
      .open(CentrexUsersFormComponent, {
        width: '700px',
        data: {
          user: enrichedUser, // 🔥 IMPORTANT
          mode,
          firstName: enriched?.firstName ?? '',
          lastName: enriched?.lastName ?? '',
          email: enriched?.email ?? user.email ?? '',
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result === 'updated') {
          const currentOrg = this.organizationsService.getCurrentOrganization();
          if (currentOrg) {
            this.loadUsers(currentOrg.id);
          }
        }
      });
  }
  onInput(event: Event, column: string): void {
    const target = event.target as HTMLInputElement;
    const value = target?.value || '';
    this.applyFilter(column, value);
  }

  getNom(user: CentrexUserModel): string {
    if (this.platform === 'Centrex NG') {
      return this.userInfosMap.get(user.id)?.lastName || '—';
    } else {
      return user?.lastname || '—';
    }
  }

  getPrenom(user: CentrexUserModel): string {
    if (this.platform === 'Centrex NG') {
      return this.userInfosMap.get(user.id)?.firstName || '—';
    } else {
      return user?.firstname || '—';
    }
  }

  getEmail(user: CentrexUserModel): string {
    return this.userInfosMap.get(user.id)?.email || user.email || '—';
  }

  isBlocked(user: CentrexUserModel): boolean {
    return user.enabled === false || user.enabled === undefined;
  }

  resetColumnFilter(column: string): void {
    this.filterValues[column] = '';
    this.dataSource.filter = JSON.stringify(this.filterValues);

    // Optionnel : si on veut aussi vider l'input affiché dans le champ
    const input = document.querySelector<HTMLInputElement>(
      `input[name="${column}"]`,
    );
    if (input) input.value = '';
  }
}
